import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  describePAApiError,
  getSpecialistFeed,
  saveMorningBrief,
  updateSpecialistConfig,
  type MorningSection,
} from '../../../lib/api/team.js';
import { skillObject } from '../../../lib/areas.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './bomdia.css';

const SECTIONS: Array<{ id: MorningSection; label: string; help: string }> = [
  { id: 'weather', label: 'Tempo', help: 'Clima e caminho' },
  { id: 'agenda', label: 'Agenda', help: 'Compromissos e lembretes' },
  { id: 'waiting', label: 'Decisões', help: 'O que espera você' },
  { id: 'bills', label: 'Contas', help: 'Próximos vencimentos' },
  { id: 'dates', label: 'Datas', help: 'Momentos próximos' },
  { id: 'news', label: 'Notícias', help: 'Seus destaques' },
  { id: 'horoscope', label: 'Horóscopo', help: 'Seu céu do dia' },
  { id: 'shopping', label: 'Lista de compras', help: 'O que está faltando' },
];
type Highlight = {
  id: MorningSection;
  label: string;
  value: string;
  detail: string;
  tone: string;
  specialistId?: string | undefined;
  items?: Array<{ label: string; value: string; tone?: string }>;
};
type Brief = { date: string; greeting: string; highlights: Highlight[]; generatedAt: string };

// Objeto que abre cada parte do resumo: o da habilidade que cuida dela.
const SECTION_ART: Record<MorningSection, string> = {
  weather: skillObject('clima'),
  bills: skillObject('contas'),
  dates: skillObject('datas_importantes'),
  shopping: skillObject('lista_compras'),
  news: skillObject('noticias'),
  horoscope: skillObject('horoscopo'),
  agenda: '/digest-icons/agenda-compromisso.webp',
  waiting: '/digest-icons/contato-novo.webp',
};
const SECTION_OBJECT: Record<MorningSection, string> = {
  agenda: '/digest-icons/agenda-compromisso.webp',
  weather: '/habilidades/clima.webp',
  waiting: '/digest-icons/contato-novo.webp',
  bills: '/news-topics/dinheiro.webp',
  dates: '/owner-facts/familia.webp',
  news: '/news-topics/principais.webp',
  horoscope: '/owner-facts/voce.webp',
  shopping: '/habilidades/lista_compras.webp',
};

function itemObject(section: MorningSection, tone?: string) {
  if (section === 'agenda' && tone === 'reminder') return '/digest-icons/agenda-lembrete.webp';
  if (section === 'agenda') return '/digest-icons/agenda-compromisso.webp';
  return SECTION_OBJECT[section];
}

function Welcome({ begin, onBack }: { begin: () => void; onBack: () => void }) {
  return (
    <PageShell
      kicker="Bom dia"
      title="Seu dia começa antes da correria"
      lead="Eu reúno o que cada área separou e entrego tudo em um único olhar."
      onBack={onBack}
    >
      <section className="pgs-section">
        <h2 className="pgs-h2">Por exemplo</h2>
        {SECTIONS.slice(0, 3).map((item) => (
          <div className="pgs-row" key={item.id}>
            <span className="pgs-ic">
              <img src={SECTION_ART[item.id]} alt="" />
            </span>
            <span className="pgs-rowt">
              <b>{item.label}</b>
              <small>{item.help}</small>
            </span>
          </div>
        ))}
      </section>
      <div className="pgs-note">
        <i aria-hidden="true">i</i>
        <span>
          <b>Você escolhe o que entra</b>
          <small>Eu organizo a ordem e deixo pronto para quando você abrir.</small>
        </span>
      </div>
      <div className="pgs-actions">
        <button type="button" className="pgs-btn solid" onClick={begin}>
          Montar meu Bom dia
        </button>
      </div>
    </PageShell>
  );
}

function Setup({
  initial,
  done,
  onBack,
}: {
  initial?: MorningSection[] | undefined;
  done: () => void;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<MorningSection[]>(
    initial?.length ? initial : SECTIONS.map((item) => item.id),
  );
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () => saveMorningBrief({ greetingName: name || undefined, sections: selected }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'bom_dia'] });
      done();
    },
    onError: (err) =>
      setError(describePAApiError(err, 'Não consegui guardar suas escolhas agora.')),
  });
  function toggle(id: MorningSection) {
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }
  return (
    <PageShell
      kicker="Bom dia"
      title="O que entra no seu resumo?"
      lead="Escolha pelo menos três partes do seu dia."
      onBack={onBack}
    >
      <section className="pgs-section">
        <h2 className="pgs-h2">
          Partes do resumo{' '}
          <small>
            {selected.length} de {SECTIONS.length}
          </small>
        </h2>
        {SECTIONS.map((item) => {
          const on = selected.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              role="switch"
              aria-checked={on}
              className="pgs-row"
              onClick={() => toggle(item.id)}
            >
              <span className="pgs-ic">
                <img src={SECTION_ART[item.id]} alt="" />
              </span>
              <span className="pgs-rowt">
                <b>{item.label}</b>
                <small>{item.help}</small>
              </span>
              <span className={`bd-switch${on ? ' on' : ''}`} aria-hidden="true">
                <i />
              </span>
            </button>
          );
        })}
      </section>

      <label className="bd-field">
        <span>Como eu chamo você pela manhã? (opcional)</span>
        <input
          className="bd-input"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          placeholder="Seu primeiro nome"
        />
      </label>
      {error && (
        <p className="bd-error" role="alert">
          {error}
        </p>
      )}
      <div className="pgs-actions">
        <button
          type="button"
          className="pgs-btn solid"
          disabled={selected.length < 3 || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Guardando suas escolhas…' : 'Preparar meu resumo'}
        </button>
      </div>
      <p className="bd-fine">Abra quando quiser começar o dia. Nada aparece sem você pedir.</p>
    </PageShell>
  );
}

function Dashboard({
  brief,
  edit,
  readSections,
  likedSections,
  onBack,
}: {
  brief: Brief;
  edit: () => void;
  readSections: MorningSection[];
  likedSections: MorningSection[];
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<MorningSection | null>(null);
  const date = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${brief.date}T12:00:00Z`));
  const interact = useMutation({
    mutationFn: ({
      id,
      read,
      liked,
    }: {
      id: MorningSection;
      read?: boolean | undefined;
      liked?: boolean;
    }) => updateSpecialistConfig('bom_dia', { interaction: { id, read, liked } }),
    onMutate: ({ id }) => setBusy(id),
    onSettled: () => {
      setBusy(null);
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'bom_dia'] });
      qc.invalidateQueries({ queryKey: ['pa-owner-facts'] });
    },
  });
  const readCount = brief.highlights.filter((item) => readSections.includes(item.id)).length;
  const day = `${date.charAt(0).toUpperCase()}${date.slice(1)}`;
  return (
    <PageShell
      kicker="Bom dia"
      title={brief.greeting}
      lead={`${day}. Juntei o que cada área separou e deixei na ordem.`}
      onBack={onBack}
      aside={
        <button type="button" className="pgs-btn small" onClick={edit}>
          Ajustar
        </button>
      }
    >
      <div className="pgs-figure">
        <b>{brief.highlights.length}</b>
        <small>
          {brief.highlights.length === 1 ? 'parte' : 'partes'} no resumo de hoje
          {readCount > 0 ? `, ${readCount} ${readCount === 1 ? 'lida' : 'lidas'}` : ''}
        </small>
      </div>

      {brief.highlights.map((item) => {
        const read = readSections.includes(item.id);
        const liked = likedSections.includes(item.id);
        return (
          <section key={item.id} className="pgs-section">
            <h2 className="pgs-h2">
              {item.label} {read && <small>Lido</small>}
            </h2>
            <div className={`pgs-row${read ? ' done' : ''}`}>
              <span className="pgs-ic">
                <img src={SECTION_ART[item.id]} alt="" />
              </span>
              <span className="pgs-rowt">
                <b>{item.value}</b>
                <small>{item.detail}</small>
              </span>
            </div>
            {item.items?.length ? (
              item.items.map((detail, itemIndex) => (
                <div
                  className={`pgs-row${read ? ' done' : ''}`}
                  key={`${detail.label}-${itemIndex}`}
                >
                  <span className="pgs-ic">
                    <img src={itemObject(item.id, detail.tone)} alt="" />
                  </span>
                  <span className="pgs-rowt">
                    <b>{detail.value}</b>
                    <small>{detail.label}</small>
                  </span>
                </div>
              ))
            ) : (
              <p className="pgs-empty">Tudo certo por aqui. Nada pede sua atenção agora.</p>
            )}
            <div className="pgs-actions">
              <button
                type="button"
                disabled={busy === item.id}
                className="pgs-btn small"
                aria-pressed={read}
                onClick={() => interact.mutate({ id: item.id, read: !read })}
              >
                {read ? 'Lido' : 'Marcar como lido'}
              </button>
              <button
                type="button"
                disabled={busy === item.id}
                className={`pgs-btn small bd-like${liked ? ' on' : ''}`}
                aria-pressed={liked}
                aria-label={liked ? 'Remover curtida' : 'Curtir este assunto'}
                onClick={() => interact.mutate({ id: item.id, liked: !liked })}
              >
                <span aria-hidden="true">{'♥︎'}</span>
                {liked ? 'Curti' : 'Gostei'}
              </button>
            </div>
          </section>
        );
      })}

      <div className="pgs-note">
        <i aria-hidden="true">“</i>
        <span>
          <b>Você pode me pedir</b>
          <small>
            “Cruza meu dia com o tempo de amanhã.” Eu chamo a equipe e junto a resposta.
          </small>
        </span>
      </div>
      <div className="pgs-actions">
        <button type="button" className="pgs-btn" onClick={edit}>
          Escolher o que entra no meu Bom dia
        </button>
      </div>
      <p className="bd-fine">Preparei agora com o que cada área separou.</p>
    </PageShell>
  );
}

export function BomDiaView() {
  const back = useSkillBack();
  const [setup, setSetup] = useState(false);
  const query = useQuery({
    queryKey: ['pa-specialist-feed', 'bom_dia'],
    queryFn: () => getSpecialistFeed('bom_dia'),
    refetchInterval: (q) => (q.state.data?.generating ? 1600 : false),
  });
  const config = query.data?.config as
    | {
        configured?: boolean | undefined;
        sections?: MorningSection[] | undefined;
        readSections?: MorningSection[] | undefined;
        likedSections?: MorningSection[] | undefined;
      }
    | undefined;
  const details = query.data?.items.find((item) => item.kind === 'morning_brief')?.details as
    Brief | undefined;

  if (query.isError) {
    return (
      <PageShell kicker="Bom dia" title="Não consegui abrir seu resumo agora" onBack={back}>
        <div className="pgs-actions">
          <button type="button" className="pgs-btn solid" onClick={() => query.refetch()}>
            Tentar novamente
          </button>
        </div>
      </PageShell>
    );
  }
  if (config && !config.configured && !setup) {
    return <Welcome begin={() => setSetup(true)} onBack={back} />;
  }
  if (setup || (config && !config.configured)) {
    return (
      <Setup
        initial={config?.sections}
        done={() => setSetup(false)}
        onBack={setup ? () => setSetup(false) : back}
      />
    );
  }
  if (config?.configured && details) {
    return (
      <Dashboard
        brief={details}
        edit={() => setSetup(true)}
        readSections={config.readSections ?? []}
        likedSections={config.likedSections ?? []}
        onBack={back}
      />
    );
  }
  if (config?.configured && !query.data?.generating) {
    return (
      <PageShell
        kicker="Bom dia"
        title="Estou preparando o seu primeiro resumo"
        lead="Junto o que cada área separou e já mostro aqui."
        onBack={back}
      >
        <div className="pgs-actions">
          <button type="button" className="pgs-btn solid" onClick={() => query.refetch()}>
            Abrir meu dia
          </button>
        </div>
      </PageShell>
    );
  }
  // Carregando, ou o servidor ainda está reunindo as áreas.
  return (
    <PageShell
      kicker="Bom dia"
      title={query.isLoading ? 'Seu resumo' : 'Estou reunindo as áreas'}
      onBack={back}
    >
      <Skeleton
        variant="rows"
        label={query.isLoading ? 'Abrindo o seu resumo' : 'Reunindo as áreas'}
      />
    </PageShell>
  );
}
